<template>
  <!-- Summary of one class for the app screens (class sheet, start and end):
       white card with a blue accent; the countdown is data, not a button. -->
  <div class="class-card" :class="'status-' + status">
    <div class="class-card-head">
      <img src="@/assets/images/avatar.svg" />
      <div class="class-card-person">
        <div class="class-card-name">{{ studentName }}</div>
        <div class="class-card-when">
          <span>{{ practice.date | humanDate(false) }} · {{ clock(practice.date) }}–{{ clock(end) }}</span>
          <span class="class-card-status">{{ s.status[status] }}</span>
        </div>
      </div>
    </div>

    <div class="class-card-countdown" v-if="countdown && countdown.value">
      <span>{{ countdown.label }}</span>
      <b>{{ countdown.value }}</b>
    </div>

    <div class="class-card-facts" v-if="facts">
      <div v-if="vehicle && vehicle.name">
        <span>{{ s.vehicle }}</span>
        <b>{{ vehicle.name }}<template v-if="vehicle.code"> · {{ vehicle.code }}</template></b>
      </div>
      <div>
        <span>{{ s.type }}</span>
        <b>{{ typeName || practice.license }} · {{ practice.time }} min</b>
      </div>
      <div v-if="practice.ended === 1 && practice.grade > 0">
        <span>{{ s.grade }}</span>
        <b class="class-card-grade" :class="'grade-' + practice.grade">{{ app.strings.practice.start.grades[practice.grade] }}</b>
      </div>
    </div>

    <div class="class-card-meeting" :class="{other: practice.meeting_point === 'other'}">
      <font-awesome-icon :icon="['fas', 'map-marker-alt']" />
      <div>
        <span>{{ s.meeting }}</span>
        <b v-if="practice.meeting_point === 'other'">{{ practice.meeting_address }}</b>
        <b v-else>{{ app.strings.practice.start.meeting_school }}</b>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'practice-class-card-component',
  props: {
    app: Object,
    practice: Object,
    student: Object,
    vehicle: Object,
    typeName: String,
    // {label, value} or null.
    countdown: Object,
    // Show vehicle, type and grade (not needed where the screen lists them).
    facts: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    s () {
      return this.app.strings.practice.menu.card
    },
    studentName () {
      let st = this.student || {}
      return [st.first_name, st.last_name_1, st.last_name_2].filter((p) => p).join(' ')
    },
    end () {
      let d = this.practice.date instanceof Date ? this.practice.date : new Date(this.practice.date)
      return new Date(d.getTime() + (parseInt(this.practice.time) || 0) * 60000)
    },
    status () {
      if (this.practice.cancelled === 1) {
        return 'cancelled'
      }
      if (this.practice.ended === 1) {
        return 'ended'
      }
      if (this.practice.started === 1) {
        return 'running'
      }
      return 'scheduled'
    }
  },
  methods: {
    clock (value) {
      let d = value instanceof Date ? value : new Date(value)
      if (isNaN(d.getTime())) {
        return ''
      }
      return (d.getHours() < 10 ? '0' : '') + d.getHours() + ':' + (d.getMinutes() < 10 ? '0' : '') + d.getMinutes()
    }
  }
}
</script>

<style lang="scss" scoped>
.class-card {
  padding: 18px;
  border: 1px solid #e8edf3;
  border-left: 4px solid #2071b7;
  border-radius: 20px;
  background-color: #FFFFFF;
  box-shadow: 0 12px 30px rgba(20, 67, 107, 0.08);
  box-sizing: border-box;
  font-family: 'Roboto', sans-serif;
  text-align: left;
}

.class-card.status-ended {
  border-left-color: #16a34a;
}

.class-card.status-cancelled {
  border-left-color: #dc2626;
}

.class-card.status-running {
  border-left-color: #d97706;
}

.class-card-head {
  display: flex;
  align-items: center;
  gap: 14px;
}

.class-card-head > img {
  flex: 0 0 auto;
  width: 54px;
  height: 54px;
  border: 2px solid rgba(32, 113, 183, 0.25);
  border-radius: 999px;
  background-color: rgba(32, 113, 183, 0.08);
  box-sizing: border-box;
}

.class-card-person {
  flex: 1;
  min-width: 0;
}

.class-card-name {
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.class-card-when {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  margin-top: 4px;
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
}

.class-card-status {
  padding: 3px 9px;
  border-radius: 999px;
  background-color: rgba(32, 113, 183, 0.1);
  color: #2071b7;
  font-family: 'Lexend', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.status-ended .class-card-status { background-color: rgba(22, 163, 74, 0.12); color: #15803d; }
.status-cancelled .class-card-status { background-color: rgba(220, 38, 38, 0.1); color: #b91c1c; }
.status-running .class-card-status { background-color: #fff4e6; color: #b45309; }

.class-card-countdown {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 14px;
  background-color: #f5f8fb;
}

.class-card-countdown > span,
.class-card-facts span,
.class-card-meeting span {
  display: block;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.class-card-countdown > b {
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 20px;
  font-variant-numeric: tabular-nums;
}

.class-card-facts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-top: 12px;
}

.class-card-facts > div {
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
}

.class-card-facts b {
  display: block;
  margin-top: 3px;
  color: #1f2a37;
  font-family: 'Lexend', sans-serif;
  font-size: 14px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.class-card-grade.grade-1 { color: #b91c1c; }
.class-card-grade.grade-2 { color: #d97706; }
.class-card-grade.grade-3 { color: #2071b7; }
.class-card-grade.grade-4 { color: #0f766e; }

.class-card-meeting {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding: 12px 14px;
  border-radius: 14px;
  background-color: #f5f8fb;
}

.class-card-meeting > svg {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 18px;
}

.class-card-meeting b {
  display: block;
  margin-top: 2px;
  color: #1f2a37;
  font-size: 15px;
  overflow-wrap: anywhere;
}

.class-card-meeting.other {
  border: 1px solid rgba(32, 113, 183, 0.35);
  background-color: rgba(32, 113, 183, 0.08);
}

.class-card-meeting.other > svg,
.class-card-meeting.other b {
  color: #2071b7;
}
</style>
